import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '../lib/cn';

/**
 * Bildmarke und Schriftzug von GrundPuls.
 *
 * Bildmarke: Hauslinie (Textfarbe) mit Herz in der Markenfarbe. Das Herz schlägt
 * im Doppelschlag eines Ruhepulses; bei jedem Schlag leuchtet ein weicher Schein
 * nach außen auf. Bei reduzierter Bewegung bleibt das Zeichen ruhig.
 *
 * Pulsstärke:
 *  - `deutlich`: Anmeldung und Startbildschirm, Blickfang
 *  - `dezent`:   Navigation und Kopfzeile, ruhig im Arbeitsalltag
 *  - `aus`:      statisch (Druck, PDF, Favicon)
 */

export type Pulsstaerke = 'deutlich' | 'dezent' | 'aus';

const HAUS = 'M12 47 L50 14 L88 47 M22 39 V86 H78 V39';
const HERZ =
  'M50 76 C50 76 34 66 34 56 C34 50 39 46 44 46 C47 46 49 48 50 50 ' +
  'C51 48 53 46 56 46 C61 46 66 50 66 56 C66 66 50 76 50 76 Z';

/** Doppelschlag (Systole, kurze Pause, zweiter Schlag), danach Ruhe. */
const SCHLAG = {
  deutlich: { skala: [1, 1.16, 1, 1.1, 1], schein: [0, 0.5, 0.15, 0.35, 0], weite: [1, 1.45, 1.3, 1.6, 1.7] },
  dezent: { skala: [1, 1.08, 1, 1.05, 1], schein: [0, 0.28, 0.08, 0.18, 0], weite: [1, 1.3, 1.2, 1.4, 1.45] },
} as const;
const ZEITPUNKTE = [0, 0.14, 0.3, 0.44, 1];
const DAUER = { deutlich: 1.15, dezent: 1.3 } as const;
const PAUSE = { deutlich: 0.55, dezent: 1.6 } as const;

/** Bildmarke mit Herz-Puls. Größe in Pixeln, Hausfarbe = aktuelle Textfarbe. */
export function Bildmarke({
  groesse = 32,
  puls = 'dezent',
  className,
  titel,
}: {
  groesse?: number;
  puls?: Pulsstaerke;
  className?: string;
  /** Zugänglicher Name; ohne Angabe ist die Marke dekorativ. */
  titel?: string;
}) {
  const reduziert = useReducedMotion();
  const aktiv = puls !== 'aus' && !reduziert;
  // Kleine Darstellungen brauchen eine kräftigere Linie, sonst verschwindet das Haus.
  const linie = groesse <= 20 ? 10 : groesse <= 36 ? 8 : 7;

  const herzStil = { transformBox: 'fill-box' as const, transformOrigin: '50% 55%' };
  const wiederholung = aktiv
    ? {
        duration: DAUER[puls as 'deutlich' | 'dezent'],
        times: ZEITPUNKTE,
        ease: 'easeInOut' as const,
        repeat: Infinity,
        repeatDelay: PAUSE[puls as 'deutlich' | 'dezent'],
      }
    : undefined;
  const schlag = aktiv ? SCHLAG[puls as 'deutlich' | 'dezent'] : null;

  return (
    <svg
      width={groesse}
      height={groesse}
      viewBox="0 0 100 100"
      className={cn('shrink-0 overflow-visible', className)}
      role={titel ? 'img' : undefined}
      aria-label={titel}
      aria-hidden={titel ? undefined : true}
    >
      <path
        d={HAUS}
        fill="none"
        stroke="currentColor"
        strokeWidth={linie}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {schlag && (
        <motion.path
          d={HERZ}
          className="fill-marke"
          style={herzStil}
          initial={{ opacity: 0, scale: 1 }}
          animate={{ opacity: [...schlag.schein], scale: [...schlag.weite] }}
          transition={wiederholung}
        />
      )}
      <motion.path
        d={HERZ}
        className="fill-marke"
        style={herzStil}
        initial={{ scale: 1 }}
        animate={schlag ? { scale: [...schlag.skala] } : { scale: 1 }}
        transition={wiederholung}
      />
    </svg>
  );
}

/** Schriftzug „GrundPuls“, „Puls“ in der Markenfarbe. */
export function Schriftzug({ className }: { className?: string }) {
  return (
    <span className={cn('font-semibold tracking-tight text-fg', className)}>
      Grund<span className="text-marke">Puls</span>
    </span>
  );
}
